import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatInUserTimezone } from "../../utils/timezone";

const POLL_MS = 60 * 1000;

function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [teamSummary, setTeamSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const lastKnownCountRef = useRef(0);
  const menuRef = useRef(null);

  const role = String(user?.role || "").trim();
  const isManagerOrAdmin = [
    "Admin",
    "SM",
    "Sales Owner",
    "Sales Manager",
  ].includes(role);

  const loadCount = useCallback(async () => {
    try {
      const res = await api.getNotificationsUnreadCount();
      const next = Number(res?.count || 0);
      setCount(next);
    } catch {
      /* silent */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getNotifications({ limit: 30 });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setItems(rows);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeamSummary = useCallback(async () => {
    if (!isManagerOrAdmin) return;
    try {
      const res = await api.getNotificationTeamSummary();
      setTeamSummary(res || null);
    } catch {
      /* silent */
    }
  }, [isManagerOrAdmin]);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (count > lastKnownCountRef.current && lastKnownCountRef.current !== 0) {
      setToast("You have a new Next Contact reminder.");
      setTimeout(() => setToast(null), 6000);
    }
    lastKnownCountRef.current = count;
  }, [count]);

  useEffect(() => {
    if (!open) return;
    loadList();
    if (isManagerOrAdmin) loadTeamSummary();
  }, [open, loadList, loadTeamSummary, isManagerOrAdmin]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleOpenItem = async (item) => {
    const wasUnread = !item.is_read || Number(item.is_read) === 0;

    if (wasUnread) {
      try {
        await api.markNotificationRead(item.notification_id);
      } catch {}
    }

    setOpen(false);

    if (wasUnread) {
      setCount((c) => Math.max(0, c - 1));
      setItems((prev) =>
        prev.map((row) =>
          row.notification_id === item.notification_id
            ? { ...row, is_read: 1 }
            : row,
        ),
      );
      try {
        await loadCount();
      } catch {}
    }

    if (item.lead_id) {
      navigate("/leads", {
        state: {
          openLeadId: item.lead_id,
          focusSection: "next-activities",
          nextActivityId: item.next_activity_id || null,
        },
      });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setCount(0);
      setItems((prev) => prev.map((i) => ({ ...i, is_read: 1 })));
    } catch {}
  };

  const goToAllNotifications = () => {
    setOpen(false);
    navigate("/notifications");
  };

  const goToTeamFor = (ownerUserId) => {
    setOpen(false);
    navigate(`/notifications?tab=team&userId=${ownerUserId}`);
  };

  /* ---------- Sectioning ---------- */

  const now = Date.now();
  const last24hMs = 24 * 60 * 60 * 1000;

  const isUnread = (i) => !i.is_read || Number(i.is_read) === 0;
  const isReminder = (i) => i.type === "reminder";
  const isWithin24h = (i) => {
    if (!i.created_at) return false;
    const t = new Date(i.created_at).getTime();
    return !Number.isNaN(t) && now - t <= last24hMs;
  };

  const needsActionItems = items
    .filter((i) => isUnread(i) && isReminder(i))
    .slice(0, 5);

  const todayActivityItems = items
    .filter((i) => !needsActionItems.includes(i) && isWithin24h(i))
    .slice(0, 5);

  /* Filter self out of the team block; backend keeps self in the summary
     so the same endpoint can be reused for a "team detail" view. */
  const teamRows = (teamSummary?.byUser || []).filter(
    (r) => Number(r.user_id) !== Number(user?.userId),
  );
  const teamUnreadTotal = teamRows.reduce(
    (sum, r) => sum + Number(r.unread_count || 0),
    0,
  );

  return (
    <div className="notification-bell-wrapper" ref={menuRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
      >
        <i className="fas fa-bell" />
        {count > 0 && (
          <span className="notification-bell-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-head">
            <strong>Notifications</strong>
            {count > 0 && (
              <button
                type="button"
                className="notification-mark-all"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-dropdown-body">
            {loading ? (
              <div style={{ padding: 14 }}>
                <i className="fas fa-spinner fa-spin" /> Loading...
              </div>
            ) : items.length === 0 && teamUnreadTotal === 0 ? (
              <div className="notification-empty">No notifications</div>
            ) : (
              <>
                {/* ============ NEEDS ACTION ============ */}
                {needsActionItems.length > 0 && (
                  <>
                    <div className="notification-section-label">
                      Needs action ({needsActionItems.length})
                    </div>
                    {needsActionItems.map((item) => (
                      <button
                        key={item.notification_id}
                        type="button"
                        className="notification-item unread"
                        onClick={() => handleOpenItem(item)}
                      >
                        <div className="notification-item-title">
                          <i
                            className="fas fa-bell"
                            style={{ color: "#d97706", marginRight: 6 }}
                          />
                          {item.title}
                        </div>
                                               <div className="notification-item-date">
                          {formatInUserTimezone(item.created_at)}
                        </div>
                        {/* R6 (additive) — action button */}
                        {item.action_url && (
                          <button
                            type="button"
                            className="r6-notif-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              navigate(item.action_url);
                            }}
                          >
                            {item.action_label || "Manage"}
                          </button>
                        )}
                      </button>
                    ))}
                  </>
                )}

                {/* ============ TEAM ============ */}
                {isManagerOrAdmin && teamRows.length > 0 && (
                  <>
                    <div className="notification-section-label">
                      Team — {teamUnreadTotal} item
                      {teamUnreadTotal === 1 ? "" : "s"} need attention
                    </div>
                    {teamRows.slice(0, 5).map((row) => (
                      <button
                        key={row.user_id}
                        type="button"
                        className="notification-item notification-team-item"
                        onClick={() => goToTeamFor(row.user_id)}
                      >
                        <div className="notification-item-title">
                          <i
                            className="fas fa-user"
                            style={{ color: "#48a0dc", marginRight: 6 }}
                          />
                          {row.full_name || "Team member"}
                        </div>
                        <div className="notification-item-body">
                          {row.unread_count} overdue reminder
                          {row.unread_count === 1 ? "" : "s"}
                          {row.oldest_created_at
                            ? ` · oldest ${formatInUserTimezone(
                                row.oldest_created_at,
                              )}`
                            : ""}
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {/* ============ TODAY'S ACTIVITY ============ */}
                {todayActivityItems.length > 0 && (
                  <>
                    <div className="notification-section-label">
                      Today's activity
                    </div>
                    {todayActivityItems.map((item) => (
                      <button
                        key={item.notification_id}
                        type="button"
                        className="notification-item"
                        onClick={() => handleOpenItem(item)}
                      >
                        <div className="notification-item-title">
                          {item.type === "digest" ? (
                            <i
                              className="fas fa-clipboard-list"
                              style={{ color: "#48a0dc", marginRight: 6 }}
                            />
                          ) : (
                            <i
                              className="fas fa-check"
                              style={{ color: "#6b7a8a", marginRight: 6 }}
                            />
                          )}
                          {item.title}
                        </div>
                        <div className="notification-item-date">
                          {formatInUserTimezone(item.created_at)}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          <div className="notification-dropdown-footer">
            <button
              type="button"
              className="notification-view-all"
              onClick={goToAllNotifications}
            >
              Open Notification Center →
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="notification-toast">
          <i className="fas fa-bell" /> {toast}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;